"use client";
import Image from "next/image";
import { useUser } from "@/lib/context/user-context";
import { useShadowingStats } from "@/hooks/useShadowingStats";
import React, { useRef, useState, useEffect, useLayoutEffect } from "react";
import { useI18n } from "@/lib/i18n";
import { Search, Medal, Star } from "lucide-react";
import { SearchDialog } from "@/components/search/search-dialog";

// 用于避免 hydration 不匹配的 hook
function useHasMounted() {
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);
  return hasMounted;
}
// import { HandWrittenTitle } from "@/components/ui/hand-writing-text"

// 头像图片组件（使用提供的图片）
function UserAvatar() {
  return (
    <div className="w-8 h-8 sm:w-10 sm:h-10 border rounded-xl overflow-hidden flex-shrink-0 relative rotate-[8deg] animate-[wiggle_1s_ease-in-out_3]">
      <Image
        src="/images/welcome-icon.png"
        alt="Welcome icon"
        fill
        sizes="(max-width: 640px) 2rem, 2.5rem"
        className="object-cover"
        priority
      />
      <style jsx>{`
        @keyframes wiggle {
          0%, 100% { transform: rotate(8deg); }
          25% { transform: rotate(18deg); }
          75% { transform: rotate(-2deg); }
        }
      `}</style>
    </div>
  );
}

export default function HeroSection() {
  const hasMounted = useHasMounted();
  const { user, isLoading: userLoading } = useUser();
  const userId = user?.id;
  const { stats, loading } = useShadowingStats(userId);
  const { t, lang } = useI18n() as any;

  const percent = (() => {
    const p = stats?.goal?.percentage ? parseFloat(stats.goal.percentage) : 0;
    if (Number.isFinite(p)) return Math.max(0, Math.min(100, Math.round(p)));
    return 0;
  })();
  const trackRef = useRef<HTMLDivElement | null>(null);
  const iconRef = useRef<HTMLDivElement | null>(null);
  const [reservedIconPx, setReservedIconPx] = useState(48);
  const [isSearchOpen, setIsSearchOpen] = useState(false);

  useLayoutEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    // 动态测量图标区域（轨道左边到图标容器右边）
    try {
      const tRect = el.getBoundingClientRect();
      const iRect = iconRef.current?.getBoundingClientRect();
      if (tRect && iRect) {
        const iconWidth = Math.max(32, Math.min(iRect.right - tRect.left + 2, el.clientWidth));
        setReservedIconPx(iconWidth);
      }
    } catch {}
    const onResize = () => {
      try {
        const tRect2 = el.getBoundingClientRect();
        const iRect2 = iconRef.current?.getBoundingClientRect();
        if (tRect2 && iRect2) {
          const iconWidth2 = Math.max(32, Math.min(iRect2.right - tRect2.left + 2, el.clientWidth));
          setReservedIconPx(iconWidth2);
        }
      } catch {}
    };
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    };
  }, []);
  const pFactor = Math.max(0, Math.min(1, percent / 100));
  const trophySrc = percent === 100 ? "/images/Trophy.png" : "/images/Trophy-2.png";

  return (
    <div className="flex flex-col md:flex-row w-full items-start md:items-end md:justify-between gap-3 md:gap-4">
    <SearchDialog open={isSearchOpen} onOpenChange={setIsSearchOpen} />
    <section className="space-y-3 md:space-y-4 pl-2 md:flex-1 w-full">
      <h1 className="text-2xl lg:text-3xl font-bold">
        <div className="flex flex-col sm:flex-row items-start sm:items-center pb-2 gap-3">
          <div className="flex items-center gap-3 w-full justify-between sm:w-auto sm:justify-start">
            {/* 只让“Count”有圆圈 */}
            <div className="flex items-center gap-2">
              <span className="font-sans tracking-tight">Make</span>
              <span className="font-sans tracking-tight text-red-500">TODAY</span>
              <span className="relative inline-block">
                {/* 圆圈装饰，仅包裹 Count */}
                <span
                  aria-hidden
                  className="p-2 pointer-events-none absolute -inset-1 rounded-full border-2 border-black/80 bg-yellow-300 dark:border-yellow-400 dark:bg-yellow-300/20 rotate-[-8deg] z-0"
                />
                <span className="font-sans tracking-tight relative z-10">Count</span>
              </span>
              <span className="font-sans tracking-tight">!</span>
            </div>
            
            <button 
              onClick={() => setIsSearchOpen(true)}
              className="sm:hidden p-2 -mr-2 active:scale-95 transition-transform"
              aria-label="Search"
            >
              <Search className="w-6 h-6 text-foreground/80" />
            </button>
            {/* <UserAvatar /> */}
          </div>
          {/* <div className="hidden sm:flex rounded-lg border px-3 py-2 inline-flex items-center gap-2 text-base lg:text-xl sm:ms-4 mt-3 lg:mt-0">
            <MapPinIcon className="size-4 lg:size-6" />
            {loading ? "Loading..." : country || "Unknown location"}
          </div> */}
        </div>
      </h1>
      {/* <p className="text-muted-foreground text-lg">
        Frontend developer and design system specialist with over 9 years of
        experience focusing on user experience and design systems to create
        user-centered designs in SaaS products.
      </p> */}

    </section>
    <section className="pt-1.5 md:pt-2 md:flex-shrink-0 w-full md:w-auto">
      {hasMounted && (userLoading || userId) && (
        <div className="w-full md:w-[460px] max-w-full">
          <div className="bg-[#fdfaf3] rounded-2xl md:rounded-2xl py-1 px-0.5 md:py-1 shadow-sm relative overflow-hidden" style={{ backgroundImage: "repeating-linear-gradient(45deg, rgba(227,230,219,0.7), rgba(227,230,219,0.7) 10px, transparent 10px, transparent 20px)" }}>
            {(userLoading || loading) ? (
              <div className="flex items-center space-x-2 md:space-x-3">
                {/* Indeterminate bar with subtle diagonal stripes (no moving white block) */}
                <div className="flex-1 relative h-9 md:h-12 rounded-full overflow-hidden">
                  <div
                    className="absolute inset-0 animate-[stripes_1.6s_linear_infinite] opacity-90"
                    style={{
                      backgroundImage:
                        'repeating-linear-gradient(45deg, rgba(244,114,182,0.55) 0 12px, rgba(244,114,182,0.35) 12px 24px)'
                    }}
                  />
                  <div className="absolute top-1/2 -translate-y-1/2 left-8 md:left-10 w-1.5 h-6 md:w-2 md:h-8 bg-pink-400/80 rounded-full animate-pulse" />
                  <style jsx>{`
                    @keyframes stripes { 0% { background-position: 0 0; } 100% { background-position: 48px 0; } }
                  `}</style>
                </div>
                <div className="bg-white rounded-full w-10 h-10 md:w-12 md:h-12 shadow-md animate-pulse" />
                <div className="rounded-xl w-10 h-10 md:w-12 md:h-12 bg-white shadow-md overflow-hidden">
                  <div className="w-full h-full bg-gray-200 animate-pulse" />
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-2 md:space-x-3">
                {/* Progress track */}
                <div
                  ref={trackRef}
                  className="relative flex-1 h-9 md:h-12 rounded-full overflow-hidden"
                  role="progressbar"
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-valuenow={percent}
                  aria-label="Today's progress"
                  style={{ ['--icon' as any]: `${reservedIconPx}px`, ['--p' as any]: pFactor }}
                >
                  {/* Fill */}
                  <div
                    className="absolute left-0 top-0 h-full bg-pink-400 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `calc(var(--icon) + (100% - var(--icon)) * var(--p))` }}
                  />
                  {/* Label content above fill */}
                  <div className="relative z-10 h-full px-1.5 md:px-[7px] flex items-center text-black">
                    <div ref={iconRef} className="rounded-full p-1 md:p-0 mr-1.5 md:mr-2">
                      <div className={`w-7 h-7 md:w-10 md:h-10 transition-all duration-800 ml-0 sm:-ml-[2px] ${percent === 100 ? 'animate-[shake_0.8s_ease-in-out_infinite]' : ''}`}>
                        <Image
                          src={trophySrc}
                          alt="Trophy icon"
                          width={72}
                          height={72}
                          sizes="(min-width: 768px) 36px, 28px"
                          quality={90}
                          className="w-full h-full object-contain"
                          priority
                        />
                      </div>
                    </div>
                    <span className="font-bold text-sm md:text-base pl-4">Today</span>
                    <span className="font-bold text-sm md:text-base">
                    {(() => {
                    if (!stats) return t('common.loading', 'Loading...');
                    const minutes = Math.round((stats.goal?.targetHours || 0) * 60);
                    if (minutes <= 0) return `${t('hero.goalLabel', 'Goal')}: 0 ${t('hero.minuteShort','min')}`;
                    if (minutes % 60 === 0) {
                      const hours = Math.round(minutes / 60);
                      const hourLabel = hours === 1 ? t('hero.hour', 'hour') : t('hero.hours', 'hours');
                      return `${t('hero.goalLabel', 'Goal')}: ${hours}${lang === 'chinese' ? '' : ' '}${hourLabel}`;
                    }
                    return `: ${minutes}${lang === 'chinese' ? '' : ' '}${t('hero.minuteShort', 'min')}`;
                  })()}
                    </span>
                  </div>
                  {/* Divider knob at the end of the fill */}
                  <div
                    className="absolute top-1/2 -translate-y-1/2 translate-x-2 w-1.5 h-6 md:translate-x-2.5 md:w-2 md:h-8 bg-pink-400 rounded-full"
                    style={{ left: `calc((var(--icon) + (100% - var(--icon)) * var(--p)) - 6px)` }}
                  />
                </div>

                {/* Percentage bubble */}
                
                <div className="bg-white rounded-full flex items-center justify-center w-10 h-10 md:w-12 md:h-12 font-bold text-gray-700 text-xs md:text-sm shadow-md">
                  {percent}%
                </div>
                {/* Avatar with wiggle on load */}
                <div className="w-10 h-10 md:w-12 md:h-12 rounded-xl border-[3px] md:border-4 border-white shadow-md overflow-hidden relative rotate-[8deg] animate-[wiggle_1s_ease-in-out_3]">
                  <img
                    alt="User avatar"
                    className="w-full h-full object-cover rounded-xl"
                    src={user?.user_metadata?.avatar_url || "/images/welcome-icon.png"}
                  />
                  <style jsx>{`
                    @keyframes wiggle {
                      0%, 100% { transform: rotate(8deg); }
                      25% { transform: rotate(18deg); }
                      75% { transform: rotate(-2deg); }
                    }
                    @keyframes shake {
                      0%, 100% { transform: rotate(0deg); }
                      20% { transform: rotate(-8deg); }
                      50% { transform: rotate(0deg); }
                      80% { transform: rotate(8deg); }
                    }
                  `}</style>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
    </div>
  );
}
